import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, Trash2, Plus, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Card, PageTitle } from "./shared";
import { Switch } from "../../components/ui/switch";

const CityCard = ({ city, onSaved }) => {
  const [c, setC] = useState(city);
  const [area, setArea] = useState("");
  const slug = city.name.toLowerCase().replace(/\s+/g, "-");

  const save = async (next) => {
    setC(next);
    try {
      await api.put(`/staff/cities/${city.id}`, { name: next.name, featured: next.featured, active: next.active, areas: next.areas });
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addArea = (e) => {
    e.preventDefault();
    if (!area.trim()) return;
    save({ ...c, areas: [...c.areas, area.trim()] });
    setArea("");
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${city.name}?`)) return;
    await api.delete(`/staff/cities/${city.id}`);
    onSaved();
  };

  return (
    <Card data-testid={`city-card-${slug}`}>
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 font-semibold">{c.name}</p>
        <button onClick={() => save({ ...c, featured: !c.featured })} className={`flex h-9 items-center gap-1 rounded-full px-3 text-xs font-medium ${c.featured ? "bg-[#ff9933]/15 text-[#a85400]" : "text-neutral-500 hover:bg-neutral-100"}`} data-testid={`city-featured-toggle-${slug}`}>
          <Star className="h-3.5 w-3.5" /> {c.featured ? "Quick button" : "In dropdown"}
        </button>
        <label className="flex items-center gap-2 text-xs text-neutral-600">
          <Switch checked={c.active} onCheckedChange={(v) => save({ ...c, active: v })} data-testid={`city-active-toggle-${slug}`} /> Visible
        </label>
        <button onClick={remove} className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 hover:bg-red-50 hover:text-red-600" data-testid={`city-delete-${slug}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {c.areas.map((a) => (
          <span key={a} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-xs">
            {a}
            <button onClick={() => save({ ...c, areas: c.areas.filter((x) => x !== a) })} aria-label={`Remove ${a}`} data-testid={`city-area-remove-${slug}-${a.toLowerCase().replace(/\s+/g, "-")}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        <form onSubmit={addArea} className="inline-flex">
          <input value={area} onChange={(e) => setArea(e.target.value)} placeholder="+ area" className="h-7 w-28 rounded-full border border-dashed border-neutral-300 px-3 text-xs outline-none focus:border-[var(--saffron)]" data-testid={`city-area-input-${slug}`} />
        </form>
      </div>
    </Card>
  );
};

export default function Cities() {
  const { data: cities = [], refetch } = useQuery({ queryKey: ["staff-cities"], queryFn: () => api.get("/staff/cities").then((r) => r.data) });
  const [name, setName] = useState("");

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/cities", { name, featured: false, active: true, areas: [] });
      setName("");
      refetch();
      toast.success("City added");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="staff-cities-page">
      <PageTitle>Cities &amp; areas</PageTitle>
      <form onSubmit={add} className="mb-5 flex gap-2" data-testid="city-create-form">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a city" className="h-11 flex-1 rounded-xl border border-neutral-200 bg-white px-3 text-sm" data-testid="city-name-input" />
        <button className="btn-saffron flex h-11 items-center gap-1 rounded-full px-5 text-sm font-semibold" data-testid="city-create-button"><Plus className="h-4 w-4" /> Add</button>
      </form>
      <div className="grid gap-3 lg:grid-cols-2">
        {cities.map((c) => <CityCard key={c.id + c.areas.length + c.featured + c.active} city={c} onSaved={refetch} />)}
      </div>
    </div>
  );
}
